import { ScreenshotDetails } from '@evinced-private/ui-common';

/**
 * Extended screenshot details that includes viewport dimensions for mobile screenshots
 */
export type MobileScreenshotDetails = ScreenshotDetails & {
	viewportWidth?: number;
	viewportHeight?: number;
};

// screenshotService is configured with scale: 0.5, compensate for it
const SERVICE_SCALE = 0.5;

/**
 * Scales bounding boxes from viewport coordinates to image coordinates
 * for use with EvScreenshotService (which has scale: 0.5 config)
 */
export const scaleMobileScreenshotDetails = async (
	screenshotDetails: MobileScreenshotDetails
): Promise<MobileScreenshotDetails> => {
	const { pageScreenshotUrl, boundingBoxes, viewportWidth, viewportHeight } = screenshotDetails;

	// If no viewport dimensions or no boxes, return as-is
	if (!boundingBoxes?.length || !viewportWidth || !viewportHeight) {
		return screenshotDetails;
	}

	try {
		const img = new Image();
		img.crossOrigin = 'anonymous';
		img.src = pageScreenshotUrl;

		await new Promise<void>((resolve, reject) => {
			img.onload = () => resolve();
			img.onerror = () => reject(new Error('Failed to load image'));
		});

		// Calculate scale from viewport to image space
		const scaleX = img.width / viewportWidth;
		const scaleY = img.height / viewportHeight;

		const scaledBoxes = boundingBoxes.map((box) => ({
			x: (box.x * scaleX) / SERVICE_SCALE,
			y: (box.y * scaleY) / SERVICE_SCALE,
			width: (box.width * scaleX) / SERVICE_SCALE,
			height: (box.height * scaleY) / SERVICE_SCALE
		}));

		return {
			...screenshotDetails,
			boundingBoxes: scaledBoxes
		};
	} catch (error) {
		// If scaling fails, return original
		return screenshotDetails;
	}
};
